import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ClassDetailPage } from '@org/ui';
import { useCallback } from 'react';

export const Route = createFileRoute('/_app/classes/$classId/block/$blockIdx')({
  component: ClassBlockRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    // Set by the "teacher commented on your exercise" notification: the
    // exercise to scroll to inside this spread.
    exercise: typeof search.exercise === 'string' && search.exercise.length > 0 ? search.exercise : undefined,
  }),
});

function ClassBlockRoute() {
  const { classId, blockIdx } = Route.useParams();
  const { exercise } = Route.useSearch();
  const navigate = useNavigate();
  const parsedBlockIdx = Number.parseInt(blockIdx, 10);
  const normalizedBlockIdx = Number.isNaN(parsedBlockIdx)
    ? undefined
    : parsedBlockIdx;

  // The class that completes a journey also completes its last unit. Open the
  // FINISHED journey's wall (journeyId — the server may already have activated
  // the next one) so the unit's selo ceremony plays first; the wall then hands
  // over to the journey celebration once the ceremony closes.
  const handleJourneyComplete = useCallback(
    ({ previousJourneyId, unitId }: { previousJourneyId: string; unitId: string }) => {
      navigate({
        to: '/journey',
        search: {
          journeyId: previousJourneyId,
          ceremony: unitId,
          celebrate: previousJourneyId,
        },
      });
    },
    [navigate],
  );

  // Keep the URL in sync with the spread being read so a refresh reopens the
  // class where the student left off. Must go through the router (raw
  // history.replaceState desyncs TanStack Router and its next navigate()
  // silently no-ops); resetScroll:false stops the sync from yanking the
  // reader's scroll position.
  const handleBlockIdxChange = useCallback(
    (idx: number) => {
      if (String(idx) === blockIdx) {
        return;
      }
      navigate({
        to: '/classes/$classId/block/$blockIdx',
        params: { classId, blockIdx: String(idx) },
        search: {},
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, classId, blockIdx],
  );

  return (
    <ClassDetailPage
      classId={classId}
      blockIdx={normalizedBlockIdx}
      onJourneyComplete={handleJourneyComplete}
      onBlockIdxChange={handleBlockIdxChange}
      scrollToExerciseId={exercise}
    />
  );
}
